import { useEffect, useState } from "react";
import { maskPhone } from "./maskPhone";
import { mayResubmit } from "../otpRail";
import { formatCountdown, resendSecondsLeft } from "../otpResend";
import { canRequestReset, type StaffSignIn } from "../signInFlow";
import { requestPasswordReset } from "../../../shared/api/auth";
import { ChangePasswordStep } from "./ChangePasswordStep";
import type { ReactNode } from "react";
import { useCopy } from "../../../shared/i18n";
import { AlertIcon, ChevronIcon, ChildrenIcon } from "../../../shared/ui/icons";
import { AuthShell } from "../../../app/layouts/AuthShell";
import {
  MfaEnrolmentBody,
  RecoveryCodesPanel,
  type MfaEnrolmentData,
} from "./MfaEnrolment";
import type {
  Door,
  DoorOutcome,
  EnrolmentOutcome,
} from "../../../app/router/routes";
import { BackButton, PasswordInput } from "../../../shared/components";
import {
  BackgroundLandscape,
  KalqyHangingSign,
  PlayCardIcon,
  TitleSparks,
} from "./LandingDecorations";
import { KALQY_CONTACT } from "../../marketing/copy";
import {
  IconArrowRight,
  IconBell,
  IconChevronLeft,
  IconDevice,
  IconGames,
  IconSchool,
  IconShield,
  IconSupport,
} from "../../../shared/console";
import {
  AuthHeading,
  AuthSplit,
  DoorTabs,
  type AuthPoint,
  type StaffDoor,
} from "../../console/public/AuthSplit";
import { FormAlert, TextInput } from "../../console/public/fields";
import { IconLock, IconMail } from "../../console/public/icons";
import {
  FIELD_BASE,
  FIELD_WITH_ICON,
  LABEL,
  LINK_QUIET,
  SUBMIT_FULL,
} from "../../console/public/styles";

/**
 * A1–A4 — the way in.
 *
 * 🔴 A1 REQUIRES NO READING ON THE CHILD PATH. One huge Play card, and the
 * adults' half is a form beside it — because an adult can read and a child
 * cannot. That asymmetry is the design, not an oversight.
 *
 * 🔴 A2 STILL ASSERTS NO ROLE. *"Role comes from the account, never a picker"*
 * — a picker that is SENT is a privilege-escalation control with a friendly
 * label: anyone who can reach the login screen could claim to be an admin, and
 * the server would be asked to disprove it.
 *
 * ⚠️ A1'S ROLE CHOOSER IS NOT THAT, AND THE DIFFERENCE IS THE WHOLE POINT. It
 * decides which FORM the panel shows — a teacher types an email and a password,
 * a parent types a phone number and waits for a code — and it reaches no
 * request. `/auth/login` is POSTed with the same two fields it always was, and
 * an account that belongs to another tier is signed straight back out by
 * `finishSignInAtDoor`. Nothing the chooser is set to can change who you are.
 *
 * 🔴 A3 ANSWERS GENERICALLY. A different message for a known and an unknown
 * number is a **phone-enumeration oracle** — and the numbers here belong to
 * parents of identified children.
 */

/* ──────────────────────────────────────── shared A2–A4 chrome ── */

/**
 * One field style for every auth input, and it exists because they had drifted.
 *
 * ⚠️ `text-dash-md`, NOT `text-dash-sm`. 15px is a table cell; these are the
 * two boxes a teacher types into on a five-year-old laptop at 9:00, and A3's
 * number field one screen over is 28px. 17px is the smallest size that does not
 * read as an afterthought beside it.
 *
 * 🔴 THE FOCUS RING IS THE BORDER TURNING MAGENTA, NOT A BLUE HALO. The
 * browser default is a cool grey-blue, and §1's "no grey, anywhere" is about
 * exactly this: one system-drawn ring is enough to make a warm palette look
 * like a form somebody forgot to style.
 */
const FIELD =
  "mt-1.5 h-12 w-full rounded-lg border border-line-strong bg-surface px-3.5 text-dash-md text-ink outline-none transition-colors duration-150 ease-dash focus:border-primary";

/**
 * ⚠️ DISABLED IS A WARM NEUTRAL FILL, NOT `opacity-50` ON MAGENTA. Half-opacity
 * magenta over cream lands on a washed-out pink that reads as a rendering bug
 * rather than a decision — and `line-strong` is the colour §7 already names for
 * a disabled control.
 */
const SUBMIT =
  "mt-7 h-12 w-full rounded-pill bg-primary text-dash-md font-bold text-primary-ink transition-colors duration-150 ease-dash enabled:hover:bg-primary-deep disabled:bg-line-strong disabled:text-ink-soft";

/** §10 — the artwork, never a typeset copy. */
function Wordmark() {
  return (
    <img
      src="/kalqy-wordmark.png"
      alt="Kalqy"
      width={2000}
      height={585}
      className="h-9 w-auto"
    />
  );
}

/**
 * The way back, and the language switch.
 *
 * 🔴 BACK GOES TO A1, NOT TO THE WEBSITE. Someone on a sign-in screen who wants
 * out is almost always on the wrong one of the three — a parent who tapped
 * "Teacher", a teacher who tapped "Parent". Returning them to the picker is one
 * tap from being right; returning them to the marketing page is three.
 */
function AuthNav() {
  const t = useCopy({
    en: { back: "Back" },
  });

  return (
    <div className="mx-auto flex w-full max-w-[62.5rem] items-center justify-between">
      <BackButton href="#/start" label={t.back} />
    </div>
  );
}

/**
 * ⚠️ ICON + COLOUR + TEXT, never colour alone (design-system.md §2). A failed
 * sign-in that is only a red sentence is invisible to a colour-blind user and
 * silent to a screen reader without the `role`.
 */
function Alert({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <p
      role="alert"
      data-testid={testId}
      className="mt-5 flex items-start gap-2 rounded-lg border border-pill-danger-line bg-pill-danger-bg px-3 py-2.5 text-dash-sm font-bold text-pill-danger-fg"
    >
      <span aria-hidden="true" className="mt-0.5 shrink-0">
        <AlertIcon size={16} strokeWidth={2.4} />
      </span>
      {children}
    </p>
  );
}

/**
 * ── one form, two chromes ──
 *
 * 🔴 A2 AND A3 NOW RENDER INSIDE A1 AS WELL AS AT THEIR OWN ADDRESSES, AND
 * THERE IS STILL ONE COPY OF EACH. A1 is a Play card beside a sign-in panel,
 * so the panel has to be the real sign-in — every step of it. A second,
 * "simpler" form on the landing screen would be a second place for the second
 * factor, the wrong-door refusal and the enrolment branch to drift apart, and
 * the copy that gets the fix is whichever one the author was looking at.
 *
 * ⚠️ SO THE ONLY THING `variant` CHANGES IS WHAT SURROUNDS THE FORM. Both
 * shells take the same three slots and neither touches state: `page` is the
 * full-height canvas with its own back link and wordmark; `panel` is a card
 * dropped into a screen that already has both.
 */
interface FormShellProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}

function AuthPageShell({ title, subtitle, footer, children }: FormShellProps) {
  return (
    <AuthShell
      nav={<AuthNav />}
      brand={<Wordmark />}
      title={title}
      subtitle={subtitle}
      footer={footer}
    >
      {children}
    </AuthShell>
  );
}

/**
 * ⚠️ NO `min-h-dvh`, NO BACKGROUND, NO BACK LINK. This sits inside A1, which
 * paints the canvas, hangs the sign and owns the one way back — a second copy
 * of any of those would be a page inside a page.
 */
function PanelShell({ title, subtitle, footer, children }: FormShellProps) {
  return (
    <section className="dash-text w-full rounded-[1.75rem] border border-line bg-surface p-6 font-dash text-ink shadow-[0_18px_40px_rgba(51,45,39,0.10)] sm:px-8 sm:py-6">
      {title ? (
        <TitleSparks
          title={typeof title === "string" ? title : ""}
          size="panel"
        />
      ) : null}
      {subtitle ? (
        <p className="mt-1 text-center text-dash-sm text-ink-soft">
          {subtitle}
        </p>
      ) : null}
      <div className={title || subtitle ? "mt-5" : ""}>{children}</div>
      {footer ? (
        <p className="mx-auto mt-4 max-w-[46ch] text-center text-dash-xs text-muted">
          {footer}
        </p>
      ) : null}
    </section>
  );
}

/**
 * ── the console chrome (Figma P3 "Sign in", 11:439) ──
 *
 * 🔴 THE STAFF DOORS' OWN ADDRESSES NOW WEAR THE ADULT CONSOLE LOOK; A1's
 * PANEL DOES NOT. `/start` is the page a four-year-old's Play card lives on,
 * so the panel keeps the playful chrome there; `#/teacher/signin`,
 * `#/school/signin` (and the operator door) are adult-only pages and take
 * the console split layout. Same three slots, same form, same state.
 */
const STAFF_POINTS: readonly AuthPoint[] = [
  {
    icon: IconSchool,
    title: "One login per school",
    body: "Kalqy sets up your school and sends your admin login.",
  },
  {
    icon: IconGames,
    title: "Games children play by moving",
    body: "One laptop and a webcam for the whole class.",
  },
  {
    icon: IconShield,
    title: "Camera never records",
    body: "Movement is read on the device and never leaves it.",
  },
];

function ConsolePageShell({
  title,
  subtitle,
  footer,
  children,
}: FormShellProps) {
  return (
    <AuthSplit
      headline="Move. Play. Learn."
      points={STAFF_POINTS}
      testId="staff-signin-console"
      topLink={{ lead: "New to Kalqy?", label: "Bring it to your school", href: "#/contact-sales" }}
    >
      {title || subtitle ? (
        <AuthHeading title={title} subtitle={subtitle} />
      ) : null}
      {children}
      {footer ? (
        <p className="mt-6 text-[12px] leading-4 text-muted">{footer}</p>
      ) : null}
    </AuthSplit>
  );
}

/** The console look's alert: icon + colour + text, announced. */
function ConsoleAlert({
  children,
  testId,
}: {
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div className="mt-4">
      <FormAlert testId={testId}>{children}</FormAlert>
    </div>
  );
}

/** Where a school admin writes for a new invitation (marketing contact block). */
const KALQY_CONTACT_EMAIL = KALQY_CONTACT.email;

/** Which chrome a form is wearing. `page` is its own address; `panel` is A1. */
type FormVariant = "page" | "panel";

export interface LandingScreenProps {
  onChild?: () => void;
  /** The **teacher** door, as a whole-page fallback where the panel is inert. */
  onStaff?: () => void;
  /**
   * 🔴 THE SCHOOL ADMIN'S OWN DOOR, AND ITS ABSENCE WAS A LOCKOUT RATHER THAN A
   * ROUTING UNTIDINESS.
   *
   * The "School admin" link under the tiles called `onStaff` — the same callback
   * as the Teacher tile — so it opened the TEACHER door. That is not a cosmetic
   * mismatch: `finishSignInAtDoor` refuses an account at the wrong door and
   * signs it straight back out, and `doorAccepts('teacher', 'ADMIN')` is false
   * because `homeFor('ADMIN')` is the school tier. **A school admin who clicked
   * the link labelled "School admin" could not sign in at all** — correct
   * password, correct authenticator code, signed back out on arrival.
   *
   * ⚠️ IT IS NOW A THIRD ENTRY IN THE PANEL'S ROLE CHOOSER, not a link at all.
   * A link sent somebody to another page to find out they were at the wrong
   * one; the chooser puts the school door beside the teacher's, in the control
   * that is already asking which of them you are. This stays as the fallback
   * that reaches the school door where the router passes no `onDoor`.
   */
  onSchoolAdmin?: () => void;

  /* ── the panel's sign-in, delegated straight to A2 and A3 ── */

  /**
   * 🔴 THE SAME HANDLERS `staff-login` GETS, BECAUSE IT IS THE SAME SCREEN.
   * The panel renders `StaffLoginScreen`, so the second factor, the enrolment
   * challenge and the wrong-door refusal all work inside it — passing only
   * `onSignIn` would leave an enrolled teacher's button spinning on a challenge
   * this screen had no way to render.
   */
  onSignIn?: (
    email: string,
    password: string,
    remember: boolean,
  ) => Promise<StaffSignIn>;
  /**
   * 🔴 THE SCHOOL DOOR'S OWN SIGN-IN, AND IT IS A DIFFERENT FUNCTION ON PURPOSE.
   * `signIn` and `signInAsSchoolAdmin` differ in the `Door` they hand
   * `finishSignInAtDoor`, which is the check that signs a teacher back out of
   * the admin tier and an operator back out of both. Feeding the chooser's
   * "School admin" into `onSignIn` would quietly make the two doors one again —
   * the exact bug the school door was built to close.
   */
  onSignInAsSchoolAdmin?: (
    email: string,
    password: string,
    remember: boolean,
  ) => Promise<StaffSignIn>;
  /** P3-16 — the forced change, for a sign-in whose password someone else chose. */
  onChangePassword?: (newPassword: string) => Promise<StaffSignIn>;
  /*
   * 🔴 THERE IS NO `onSignUp` HERE, AND ITS ABSENCE IS THE DECISION — now on
   * every door, not just this one (P1-14, D7). `admin-panel.md` §3 is that *"a
   * school cannot mint its own first admin"* — provisioning is a conversation,
   * and the public page that starts it is the enquiry. A self-serve button says
   * the opposite of the thing the product does.
   */
  onSubmitCode?: (code: string) => Promise<DoorOutcome>;
  onCancelCode?: () => void;
  onStartEnrolment?: () => Promise<MfaEnrolmentData>;
  onFinishEnrolment?: (code: string) => Promise<EnrolmentOutcome>;
  onEnterAfterEnrolment?: () => void;
  onDoor?: (door: Door) => void;

  /** A3's rail, so the panel can say where the code will arrive. */
  parentViaSms?: boolean;
  /** A3's request, resolving once the OTP has been ACCEPTED. */
  onRequestParentCode?: (phone: string) => Promise<void>;
  /** Given the E.164 number; the router hops to A4. */
  onParentSubmit?: (phone: string) => void;
}

/**
 * A1 — *"who's here today?"*, answered in one panel instead of three tiles.
 *
 * 🔴 THE CHILD'S CARD IS STILL THE ONLY THING ON THIS SCREEN THAT NEEDS NO
 * READING, and it is still the largest. A four-year-old cannot read "Play", so
 * that card has to win on size, colour and weight before a single word is
 * parsed — which is exactly why the adults' half could be collapsed and the
 * child's could not.
 *
 * 🔴 THE TWO ADULT TILES BECAME ONE FORM, AND THAT REMOVED A WHOLE CLASS OF
 * WRONG TURN. "Teacher" and "Parent" were two pictures that each opened a
 * DIFFERENT screen a tap later; the person who chose wrong found out only after
 * arriving somewhere asking for the wrong credential — a parent staring at a
 * password field for an account they have never had. The chooser now sits
 * directly above the fields it changes, so the mistake and its correction are
 * the same control.
 *
 * ⚠️ AND IT IS A FORM SWITCH, NOT A CLAIM. See `StaffLoginScreen`'s docblock:
 * nothing the chooser is set to reaches a request, and an account that belongs
 * to another tier is signed straight back out on arrival.
 */
export function LandingScreen({
  onChild,
  onStaff,
  onSchoolAdmin,
  onSignIn,
  onSignInAsSchoolAdmin,
  onChangePassword,
  onSubmitCode,
  onCancelCode,
  onStartEnrolment,
  onFinishEnrolment,
  onEnterAfterEnrolment,
  onDoor,
  parentViaSms = false,
  onRequestParentCode,
  onParentSubmit,
}: LandingScreenProps) {
  const t = useCopy({
    en: {
      child: "Play",
      childHint: "Tap to start",
      back: "Back to Kalqy",
      roleLabel: "Role",
      /*
       * ⚠️ IT STARTS UNCHOSEN, AND THE FIELDS BELOW IT DO NOT. Defaulting the
       * chooser to "Teacher" would sign a parent in — or rather refuse them —
       * at a door they never picked, and the refusal for that is deliberately
       * the same generic sentence as a wrong password. Showing the commonest
       * form while the button waits for an answer asks the question once,
       * plainly, instead of guessing it.
       */
      rolePlaceholder: "Choose your role",
      roleTeacher: "Teacher",
      roleParent: "Parent",
      /*
       * 🔴 THE THIRD DOOR, IN THE CHOOSER RATHER THAN UNDER IT. It was a text
       * link — first below the tiles, then inside the panel — and a link is a
       * worse answer to "which of these am I?" than an option in the control
       * already asking. `doorAccepts('teacher', 'ADMIN')` is false, so an admin
       * who answers "Teacher" here is refused after a correct password; the
       * chooser is where that mistake is cheapest to not make.
       *
       * ⚠️ IT IS STILL NOT A CLAIM. Choosing it selects `signInAsSchoolAdmin`,
       * which differs from `signIn` in the `Door` it CHECKS the account
       * against — a stricter path, not a privileged one.
       */
      roleSchool: "School admin",
    },
  });

  /** `''` until answered — see `rolePlaceholder`. */
  const [role, setRole] = useState<"" | "teacher" | "school" | "parent">("");

  /*
   * 🔴 HELD HERE RATHER THAN IN THE FORM, SO CHOOSING A ROLE DOES NOT ERASE IT.
   * `staffDoor` below folds the unchosen `''` into `'teacher'`, so a first pick
   * of "School admin" flips `key={staffDoor}` and remounts the whole staff form
   * — taking the address somebody had already typed with it, while a first pick
   * of "Teacher" left it alone. The key is still what clears the password and a
   * stale refusal between doors; only this one field is lifted out of its reach.
   */
  const [staffEmail, setStaffEmail] = useState("");

  /*
   * ⚠️ UNCHOSEN SHOWS THE TEACHER'S FORM AND SUBMITS NOTHING (`blocked`). The
   * two staff doors take the same two fields, so the panel can render them
   * before the question is answered; what it must not do is pick a door on
   * somebody's behalf, because the refusal for the wrong one is deliberately
   * the same generic sentence as a wrong password.
   */
  const staffDoor = role === "school" ? "school" : "teacher";

  /*
   * Where a WRONG-DOOR refusal points — "Go to the right page", which is the
   * only remaining use of `onDoor` here now that the standing link is gone (see
   * `StaffLoginScreen`'s `variant`). It still matters: an operator's account is
   * not in this chooser at all, and its own door is on another hostname.
   */
  const door =
    onDoor ??
    ((onSchoolAdmin ?? onStaff)
      ? (next: Door) => {
          if (next === "school") onSchoolAdmin?.();
          else onStaff?.();
        }
      : undefined);

  /*
   * 🔴 ONE NODE, HANDED TO WHICHEVER FORM IS SHOWING. Both A2 and A3 render it
   * in the same place above their first field, so the chooser does not jump
   * half a card when the answer changes — and there is only one of it, so the
   * two forms cannot disagree about what is selected.
   */
  const roleField = (
    <div className="mb-5">
      <label
        className="block text-dash-xs font-bold text-muted"
        htmlFor="landing-role"
      >
        {t.roleLabel}
      </label>
      <div className="relative mt-1.5">
        {/*
          🔴 THE SET'S OWN PEOPLE GLYPH, NOT A DRAWN ONE (design-system.md §6).
          `icons.tsx` is generated and CI-checked precisely so a screen cannot
          quietly introduce a twenty-first icon with a different stroke.
        */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-muted"
        >
          <ChildrenIcon size={20} />
        </span>
        <select
          id="landing-role"
          data-testid="role"
          value={role}
          onChange={(e) => {
            setRole(e.target.value as "" | "teacher" | "school" | "parent");
          }}
          className={`h-12 w-full appearance-none rounded-lg border border-line-strong bg-surface pr-10 pl-11 text-dash-md outline-none transition-colors duration-150 ease-dash focus:border-primary ${
            role === "" ? "text-muted" : "text-ink"
          }`}
        >
          <option value="" disabled>
            {t.rolePlaceholder}
          </option>
          <option value="teacher">{t.roleTeacher}</option>
          <option value="school">{t.roleSchool}</option>
          <option value="parent">{t.roleParent}</option>
        </select>
        {/*
          ⚠️ THE NATIVE ARROW IS REPLACED, FOR `DashUI`'s `Select` REASON. A bare
          `<select>` renders the platform's chrome — on Windows a grey-blue box
          with a grey triangle — which is the one colour §1 forbids anywhere in
          this product, and this is the first control a school ever sees.
        */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 right-3.5 -translate-y-1/2 rotate-90 text-muted"
        >
          <ChevronIcon size={16} />
        </span>
      </div>
    </div>
  );

  return (
    <div className="relative flex min-h-screen flex-col justify-between overflow-x-hidden bg-[#FAF5EE] pb-20 font-sans text-[#332D27] select-none sm:pb-[min(7rem,6dvh)]">
      {/* Hanging Kalqy Logo Banner — anchored to the very top of the page */}
      <KalqyHangingSign />

      {/* Top Bar with round back arrow */}
      <header className="relative z-20 flex items-center justify-between px-6 pt-5 pb-2">
        <BackButton href="#/" label={t.back} />
      </header>

      {/*
        ⚠️ THE `sm:` PADDING IS CAPPED AGAINST THE VIEWPORT, AND IT IS ONE OF
        TWO NUMBERS THAT MOVE TOGETHER. This band is the room reserved for
        `KalqyHangingSign`, which hangs from `top-0` across it and is itself
        capped at `26dvh`; the row below has to clear it at every height. Change
        one and check the other — the failure is a form printed across a wooden
        board.

        🔴 IT IS CAPPED AT ALL BECAUSE THIS IS THE SCREEN EVERY USER MEETS FIRST.
        At a flat top padding the tiles this replaced came to 778px on a 625px
        laptop viewport and had to be scrolled to be answered.
      */}
      <main className="relative z-10 flex flex-1 flex-col items-center justify-start px-4 pt-[8.5rem] sm:pt-[min(11.5rem,10dvh)]">
        <div className="grid w-full max-w-[57rem] grid-cols-1 justify-center gap-8 lg:grid-cols-[minmax(0,21rem)_minmax(0,27.5rem)] lg:items-center lg:gap-12">
          {/* The child's way in — no reading required. */}
          <button
            type="button"
            data-testid="tile-child"
            onClick={onChild}
            className="group relative flex min-h-[19rem] flex-col items-center justify-between overflow-hidden rounded-[2rem] border-4 border-[#F01E5D] bg-gradient-to-b from-[#FF3B7E] to-[#FA2668] p-6 text-white shadow-[0_10px_0_#D0144B,0_16px_30px_rgba(250,38,104,0.3)] transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_14px_0_#D0144B,0_20px_35px_rgba(250,38,104,0.4)] active:translate-y-1 active:shadow-[0_4px_0_#D0144B] sm:min-h-[min(21rem,34dvh)] lg:aspect-square lg:min-h-0"
          >
            {/* Subtle background star accents */}
            <div className="absolute top-4 left-4 text-white/20">
              <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                <path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7-6.3-4.6-6.3 4.6 2.3-7-6-4.6h7.6z" />
              </svg>
            </div>
            <div className="absolute top-8 right-6 text-white/20">
              <svg className="h-4 w-4 fill-current" viewBox="0 0 24 24">
                <path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7-6.3-4.6-6.3 4.6 2.3-7-6-4.6h7.6z" />
              </svg>
            </div>
            <div className="absolute right-4 bottom-16 text-white/15">
              <svg className="h-7 w-7 fill-current" viewBox="0 0 24 24">
                <path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7-6.3-4.6-6.3 4.6 2.3-7-6-4.6h7.6z" />
              </svg>
            </div>

            {/* Bottom wave curve decoration */}
            <div className="pointer-events-none absolute right-0 bottom-0 left-0 h-8 opacity-20">
              <svg
                className="h-full w-full fill-white"
                viewBox="0 0 300 40"
                preserveAspectRatio="none"
              >
                <path d="M0,20 Q75,35 150,20 T300,20 L300,40 L0,40 Z" />
              </svg>
            </div>

            <div className="flex flex-1 flex-col items-center justify-center">
              <PlayCardIcon />
            </div>

            <div className="z-10 text-center">
              <div className="font-kid text-3xl font-extrabold text-white drop-shadow-sm sm:text-4xl">
                {t.child}
              </div>
              <div className="mt-1 text-sm font-semibold text-pink-100 opacity-95">
                {t.childHint}
              </div>
            </div>
          </button>

          {/*
            🔴 THE ADULTS' HALF IS A2 AND A3 THEMSELVES, NOT A COPY OF THEM.
            Every branch those screens own — the second factor, the enrolment
            challenge, the wrong-door refusal, A3's generic answer — happens
            here, in the same code that runs at `#/teacher/signin` and
            `#/parent/phone`. A landing-page form that handled "the easy case"
            would be a second sign-in to keep in step with the first.
          */}
          <div data-testid="signin-panel">
            {role === "parent" ? (
              <ParentPhoneScreen
                variant="panel"
                roleField={roleField}
                viaSms={parentViaSms}
                onRequestCode={onRequestParentCode}
                onSubmit={onParentSubmit}
              />
            ) : (
              <StaffLoginScreen
                /*
                 * 🔴 KEYED ON THE DOOR, SO SWITCHING ROLE EMPTIES THE FORM.
                 * Without it a refusal from the teacher door would still be on
                 * screen under "School admin" — a sentence about an attempt
                 * that is no longer the one being made — and the password typed
                 * at the previous door would stay in state, which is the one
                 * thing `refuseDoor` already goes out of its way to prevent.
                 */
                key={staffDoor}
                variant="panel"
                door={staffDoor}
                roleField={roleField}
                /* Lifted above the key, so the remount cannot take it. */
                email={staffEmail}
                onEmailChange={setStaffEmail}
                /* Unchosen: the fields are shown, the button waits. */
                blocked={role === ""}
                onSignIn={role === "school" ? onSignInAsSchoolAdmin : onSignIn}
                onChangePassword={onChangePassword}
                onSubmitCode={onSubmitCode}
                onCancelCode={onCancelCode}
                onStartEnrolment={onStartEnrolment}
                onFinishEnrolment={onFinishEnrolment}
                onEnterAfterEnrolment={onEnterAfterEnrolment}
                onDoor={door}
              />
            )}
          </div>
        </div>
      </main>

      {/* Landscape Background Hills & Flowers */}
      <BackgroundLandscape />
    </div>
  );
}

/**
 * A2 — one screen for every staff role, and §10.0's sign-in wired at last.
 *
 * 🔴 IT USED TO TAKE AN EMAIL AND RETURN IT. `AppRouter` recorded the gap in as
 * many words — *"`StaffLoginScreen` TAKES AN EMAIL AND NOT A SESSION… wiring the
 * actual POST is a change to that screen"* — and `POST /auth/login` has existed
 * since §10.0. Every dashboard behind it was therefore unreachable: `mayNavigate`
 * has no role to check, so a signed-out browser bounced off every guarded route.
 * That is the whole of OD-89's failure with one extra step.
 *
 * 🔴 ONE GENERIC REFUSAL FOR EVERY WRONG ANSWER. The server answers identically
 * for an unknown email, a wrong password, a deactivated account and a parent —
 * *"a form that distinguishes them is a directory of a school's staff"* — and
 * this screen must not undo that by inferring more than it was told. `client.ts`
 * discards response bodies, so the only input is a status.
 *
 * ⚠️ 429 IS THE ONE EXCEPTION, AND IT IS NOT AN ENUMERATION LEAK. Login is
 * limited to five a minute per IP, and a school where six teachers sign in at
 * 9:00 through one connection will meet it. "Wait a minute" and "check your
 * password" are opposite actions, and the rate limit says nothing about whether
 * any account exists.
 */
export function StaffLoginScreen({
  door = "teacher",
  variant = "page",
  roleField,
  blocked = false,
  email: emailProp,
  onEmailChange,
  onSubmit,
  onSignIn,
  onChangePassword,
  onSubmitCode,
  onCancelCode,
  onStartEnrolment,
  onFinishEnrolment,
  onEnterAfterEnrolment,
  onDoor,
}: {
  /**
   * 🔴 WHICH DOOR THIS IS — AND IT CHANGES COPY, NOT BEHAVIOUR. The screen still
   * asserts nothing: it POSTs the same `/auth/login` and the account decides.
   * The router holds the rule about who may complete a sign-in here.
   *
   * ⚠️ IT DEFAULTS TO `'teacher'` FOR `DashComponentSheet`, which renders this
   * screen with no API and no router at all.
   */
  door?: "teacher" | "school";
  /**
   * Its own address, or A1's panel. See `PanelShell`.
   *
   * ⚠️ IT CHANGES THE CHROME AND NOTHING ELSE. Every step — password, code,
   * enrolment, recovery, wrong door — renders in whichever shell it is given,
   * because A1's panel is the real sign-in and not a shortcut to one.
   */
  variant?: FormVariant;
  /**
   * A1's role chooser, rendered above the email field.
   *
   * 🔴 IT IS A NAVIGATION CONTROL, NOT A CLAIM. Nothing here is sent to the
   * server: choosing "Teacher" decides which FORM A1 shows, and the account
   * still decides the role — `finishSignInAtDoor` signs a mismatched account
   * back out. That distinction is the whole reason this arrives as an opaque
   * node rather than as a `role` this screen could be tempted to submit.
   */
  roleField?: ReactNode;
  /**
   * Hold the submit — A1 renders the fields before a role has been chosen, so
   * the button must not act on a form whose destination is not yet decided.
   */
  blocked?: boolean;
  /**
   * The email, lifted OUT of this screen so it survives the `key={staffDoor}`
   * remount above. Optional: omitted, the screen keeps its own state, which is
   * what the standalone `/teacher/signin` route and `DashComponentSheet` want.
   *
   * 🔴 WITHOUT THIS, CHOOSING "SCHOOL ADMIN" EMPTIED THE FORM SOMEBODY HAD JUST
   * FILLED IN. `staffDoor` folds the unchosen `''` into `'teacher'`, so a first
   * pick of School admin flipped the key `teacher -> school` and React threw the
   * whole subtree away — while a first pick of Teacher changed nothing and kept
   * the fields. Same screen, same actions, opposite outcome depending on which
   * role you chose, and the Sign in button then sat disabled over two empty
   * boxes with no message. It read as "the button does nothing".
   *
   * ⚠️ THE KEY STAYS, AND ONLY THE EMAIL IS RESCUED. The remount is what clears
   * the PASSWORD typed at the previous door and any refusal still on screen —
   * the two things the comment on `key={staffDoor}` exists to prevent carrying
   * across. An address is not a credential and says nothing about which door it
   * was typed at, so it is the one field that may safely survive.
   */
  email?: string;
  /** Required with `email` — together they make it a controlled field. */
  onEmailChange?: (email: string) => void;
  /** ⚠️ Kept for `DashComponentSheet`, which renders this screen with no API. */
  onSubmit?: (email: string) => void;
  /**
   * Given both credentials.
   *
   * ⚠️ IT RESOLVES `'mfa'` WITHOUT THE ROUTER NAVIGATING when the account has an
   * authenticator. Before the second factor existed, resolving always meant this
   * screen had already been replaced — so a `Promise<void>` would leave the
   * button spinning forever for every enrolled account.
   *
   * 🔴 AND IT RESOLVES `'wrong-door'` RATHER THAN REJECTING, BECAUSE THAT IS NOT
   * A FAILURE. The credentials matched. Rendering "that email and password did
   * not match" at somebody whose email and password matched is how a school
   * admin ends up resetting a password that was never wrong.
   */
  onSignIn?: (
    email: string,
    password: string,
    /**
     * P0-13 / D9 — the "Keep me signed in" box, unticked by default. It decides
     * where the router stores the pair and how long the server lets it live
     * (12 hours, this tab, unticked; 30 days ticked). Not a privilege: it can
     * only make the caller's own session shorter or longer.
     */
    remember: boolean,
  ) => Promise<StaffSignIn>;
  /**
   * P3-16 — the forced change. Resolves with how the sign-in continues; the
   * router holds the change token, the screen holds two text boxes.
   */
  onChangePassword?: (newPassword: string) => Promise<StaffSignIn>;
  /**
   * The second step. The screen holds a text box; the router holds the
   * five-minute assertion — no password stays in component state while somebody
   * unlocks a phone.
   */
  onSubmitCode?: (code: string) => Promise<DoorOutcome>;
  /** Back to the password form, discarding the assertion. */
  onCancelCode?: () => void;
  /**
   * The compulsory first enrolment — reachable from THIS door too.
   *
   * 🔴 THE SERVER DECIDES AN ENROLMENT CHALLENGE FROM THE ACCOUNT, NOT FROM THE
   * ADDRESS, so an operator can still reach one here — and is refused only once
   * it completes, never before. Without this the challenge would come back to a
   * screen that renders nothing for it and a button that spins forever. The UI
   * itself is `MfaEnrolment.tsx`, shared with the operator door so the
   * scan-then-enable ordering cannot drift between two copies.
   */
  onStartEnrolment?: () => Promise<MfaEnrolmentData>;
  onFinishEnrolment?: (code: string) => Promise<EnrolmentOutcome>;
  onEnterAfterEnrolment?: () => void;
  /** Where a wrong-door arrival should have gone. Rendered as the way out. */
  onDoor?: (door: Door) => void;
  /*
   * ⛔ `onSignUp` STOOD HERE — the school door's "Or create a school account
   * yourself". P1-14 / D7 removed it: Kalqy creates a school's first login after
   * approval (the enquiry box below says so), and a self-serve link beside that
   * sentence contradicted it. The server answers the sign-up routes 404 unless
   * `SCHOOL_SELF_SIGNUP_ENABLED` is set.
   */
}) {
  /*
   * ⚠️ CONTROLLED WHEN A1 OWNS THE EMAIL, UNCONTROLLED EVERYWHERE ELSE. The
   * panel on `/start` passes both props so the address outlives the door
   * remount; the standalone routes and `DashComponentSheet` pass neither and
   * keep the local state this screen has always had. Both props or neither —
   * an `email` without `onEmailChange` would render a field that cannot be
   * typed into, so the fallback is deliberately keyed on the SETTER.
   */
  const [ownEmail, setOwnEmail] = useState("");
  const setEmail = onEmailChange ?? setOwnEmail;
  const email = onEmailChange ? (emailProp ?? "") : ownEmail;
  const [password, setPassword] = useState("");
  /* A state of this screen, not a route — `kidFlow.ts`'s rule: a five-minute
     flow that cannot survive a refresh is a state machine, not a URL. */
  const [step, setStep] = useState<
    "password" | "code" | "enrol" | "recovery" | "change-password" | "forgot"
  >("password");
  /**
   * P0-13 / D9 — "Keep me signed in", UNTICKED BY DEFAULT. These doors are used
   * on shared classroom laptops; the safe answer is the one nobody has to
   * remember to choose.
   */
  const [remember, setRemember] = useState(false);
  const [enrolment, setEnrolment] = useState<MfaEnrolmentData | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [code, setCode] = useState("");
  const [state, setState] = useState<
    | "idle"
    | "signing-in"
    | "refused"
    | "throttled"
    | "failed"
    /**
     * 🔴 THE SAME SPLIT `OperatorLoginScreen` MAKES, AND FOR THE SAME 503.
     * `/auth/login/mfa` answers 503 when the stored authenticator secret will
     * not decrypt under `TOTP_ENC_KEY`; every staff account with a factor
     * reaches it through this door too. A server that answered is not a
     * connection that failed, and only one of the two is worth retrying.
     */
    | "server-fault"
    | "code-refused"
    | "wrong-door"
  >("idle");
  /**
   * Where the account that just signed in actually belongs.
   *
   * ⚠️ IT IS SET ONLY ALONGSIDE `state === 'wrong-door'`, so it can never point
   * somewhere while the form is showing an ordinary refusal.
   */
  const [rightDoor, setRightDoor] = useState<Door | null>(null);
  /** P3-16 — the forgot-password step's own address box and outcome. */
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotState, setForgotState] = useState<
    "idle" | "sending" | "sent" | "throttled" | "failed"
  >("idle");

  const t = useCopy({
    en: {
      title: "Sign in",
      /*
       * 🔴 THE SUBTITLE NAMED ALL THREE AUDIENCES AND IS NOW FALSE. It read
       * *"Teachers, school admins and operators all sign in here"*, which was
       * accurate while `#/signin` was the only staff door and is a lie now that
       * an operator who tries it is signed back out. A sentence that invites
       * somebody through a door that will refuse them is worse than none.
       *
       * ⚠️ NAMING THE AUDIENCE IS STILL NOT A ROLE PICKER. Nothing here is sent
       * to the server and nothing is asserted; the account still decides. It
       * closes the real question somebody has after tapping "Teacher" on A1 and
       * seeing only the word "Sign in".
       */
      subtitleTeacher: "For teachers and coordinators.",
      subtitleSchool: "For school admins and principals.",
      email: "Email",
      password: "Password",
      submit: "Sign in",
      signingIn: "Signing in…",
      /* 🔴 One sentence for four different refusals. Deliberately. */
      refused: "That email and password did not match.",
      throttled: "Too many attempts from here. Wait a minute and try again.",
      failed: "Couldn’t reach Kalqy. Check the connection and try again.",
      /* ⚠️ NO "TRY AGAIN", because a retry cannot fix a deployment fault. */
      serverFault:
        "Something is wrong at Kalqy’s end, and signing in again will not fix it. Ask your school administrator or Kalqy support to check the server.",
      /*
       * Accurate, not reassuring-sounding: `InviteService` requires the account
       * to exist already and keeps it deactivated until the invitation is
       * claimed, so "ask your school admin" is genuinely the only route in.
       */
      note: "Your role comes from your account — there is nothing to choose here. No account yet? Your school admin sends an invitation.",
      /*
       * ⚠️ THE SAME FACT, MINUS THE HALF THAT WOULD NOW BE FALSE. A1's panel
       * has a role chooser two fields up, so "there is nothing to choose here"
       * would be read as being about that control — and it is not: the chooser
       * picks a form, the account still picks the role. Saying less is the only
       * honest edit; saying it twice, differently, is how copy starts lying.
       */
      notePanel: "No account yet? Your school admin sends an invitation.",
      /*
       * ── the wrong door ──
       *
       * 🔴 IT SAYS THE DETAILS WERE RIGHT, FIRST AND PLAINLY. Somebody who is
       * told "that did not match" when it did will change a password that was
       * never wrong, and a school admin who does that has locked themselves out
       * of the tier they were trying to reach.
       *
       * ⚠️ IT DOES NOT NAME THE ACCOUNT'S ROLE IN THE ABSTRACT — it names the
       * page they should be on, because that is the only thing they can act on.
       */
      wrongDoorTitle: "Right details, wrong page",
      wrongDoorSignedOut: "You have not been signed in here.",
      wrongDoorTeacher:
        "This is a teacher account. Teachers sign in on the teacher page.",
      wrongDoorSchool:
        "This is a school admin account. Admins sign in on the school admin page.",
      wrongDoorOperator:
        "This is a Kalqy platform account. Platform operators sign in on the operator page.",
      wrongDoorGo: "Go to the right page",
      /*
       * 🔴 THE TWO SCHOOL DOORS POINT AT EACH OTHER, AND THEY HAVE TO. A1 offers
       * ONE staff tile, so whichever door it opens is the wrong one for half the
       * people who tap it — and without this link their only remedy is to be
       * refused after typing a correct password and then read an address.
       *
       * ⚠️ NEITHER LINKS TO THE OPERATOR DOOR, and that asymmetry is deliberate
       * and pre-existing: the operator tier is on its own hostname, and a link
       * here would advertise an address a school has no reason to know. The
       * operator door offers the school one, not the other way round.
       */
      otherDoorFromTeacher: "Are you a school admin? Sign in here.",
      otherDoorFromSchool: "Are you a teacher? Sign in here.",
      /*
       * 🔴 THE SIGN-UP DOOR, AND IT IS ON THE SCHOOL FORM ONLY. A teacher's
       * account is created by their school admin (`InviteService`), so offering
       * "create an account" on the teacher door would invite somebody to make a
       * second, unlinked school for themselves — which is exactly the confusion
       * `admin-panel.md` §3's operator-only provisioning exists to avoid.
       */
      /* ── the console page (Figma P3) ── */
      consoleSubtitle: "Choose who you are, then use your work email.",
      workEmail: "Work email",
      emailPlaceholder: "you@school.edu.in",
      forgot: "Forgot password?",
      remember: "Keep me signed in",
      /* Read by a screen reader with the box; the visible label stays Figma's. */
      rememberHint:
        "Leave this off on a shared school laptop. You will be signed out when this tab closes, and after 12 hours at most.",
      /* ── P3-16 · forgot password ── */
      forgotTitle: "Reset your password",
      forgotSubtitle:
        "Type the email you sign in with. If it has a Kalqy login, we will email you a link to choose a new password.",
      forgotSubmit: "Email me a link",
      forgotSending: "Sending…",
      forgotBack: "Back to sign in",
      /*
       * 🔴 ONE SENTENCE WHETHER OR NOT THE ADDRESS HAS A LOGIN. The server
       * answers the same 200 for both, and a screen that said "no such account"
       * would be a directory of a school's staff.
       */
      forgotSentTitle: "Check your email",
      forgotSent: (email: string) =>
        `If ${email} has a Kalqy login, we have emailed a link to choose a new password. It works once, for 60 minutes.`,
      /* Who can still help when no mail comes — the old note, kept as the fallback. */
      forgotSentTeacher:
        "Nothing arrived? Check spam, or ask your school admin to set a new password for you.",
      forgotSentSchool: `Nothing arrived? Check spam, or email ${KALQY_CONTACT_EMAIL} from your school address.`,
      enquiryLead: "New school?",
      enquiryLink: "Send an enquiry",
      enquiryTail: "— Kalqy creates your login after approval.",
      /* ── the second factor ── */
      codeTitle: "Authenticator code",
      codeSubtitle:
        "Open your authenticator app and type the 6-digit code it is showing. It changes every 30 seconds.",
      codeLabel: "Code",
      /* One box for both — somebody reaching for a recovery code cannot produce
         an authenticator one, and asking them to classify their own problem
         first is the moment they are least able to. */
      codeHint:
        "A recovery code works here too, if you cannot reach your phone.",
      codeBack: "Start again",
      /* Both causes in one sentence: `client.ts` discards the body, so the
         screen genuinely cannot tell an expired sign-in from a wrong code. */
      codeRefused:
        "That code did not match, or the sign-in took too long. Try the current code, or start again.",
    },
  });

  const busy = state === "signing-in";
  /* `page` wears the console look (Figma P3); A1's `panel` keeps its own. */
  const pageLook = variant === "page";
  const Shell = pageLook ? ConsolePageShell : PanelShell;
  const Warn = pageLook ? ConsoleAlert : Alert;
  const labelClass = pageLook
    ? LABEL
    : "block text-dash-xs font-bold text-muted";
  const submitClass = pageLook ? `${SUBMIT_FULL} mt-6` : SUBMIT;
  const linkClass = pageLook
    ? LINK_QUIET
    : "rounded-pill px-3 py-1.5 text-dash-xs font-bold text-ink-soft underline decoration-line-strong underline-offset-4 transition-colors duration-150 ease-dash hover:text-ink";
  /*
   * 🔴 NEVER "Kalqy team" HERE, ON ANY HOST. The product owner's rule: Kalqy's
   * own login is a different page (`#/operator`, OperatorLoginScreen), not the
   * school login with a third tab. This page is for schools only.
   */
  const doors: readonly StaffDoor[] = ["school", "teacher"];

  /**
   * 🔴 THE ONE PLACE A WRONG DOOR IS RENDERED, SHARED BY ALL THREE PATHS —
   * password, code and enrolment. Three copies would be three chances for one of
   * them to leave the form sitting in `signing-in` with a spinner and no message,
   * which is exactly the failure the enrolment path shipped with once already.
   *
   * ⚠️ IT CLEARS THE PASSWORD. The credentials were correct, so what is in that
   * field is a live credential for another tier; leaving it in a form somebody is
   * about to walk away from is the one thing this state must not do.
   */
  function refuseDoor(right: Door | null) {
    setPassword("");
    setCode("");
    setEnrolment(null);
    setStep("password");
    setRightDoor(right);
    setState("wrong-door");
  }

  /**
   * What happens after the server has accepted a password — on the sign-in form
   * and again after a forced change, which continues the same sign-in.
   *
   * Deliberately no success state on `'signed-in'`: the router replaces this
   * screen the moment the session exists. A "signed in!" flash would be a frame
   * of chrome between a password and a dashboard.
   */
  async function follow(outcome: StaffSignIn): Promise<void> {
    if (outcome.at === "wrong-door") {
      refuseDoor(outcome.door);
      return;
    }
    if (outcome.at === "signed-in") return;

    /* 🔴 The password is dropped here rather than kept for a re-submit — the
       router holds the assertion instead, so nothing on this screen is a
       credential once the next step opens. */
    setPassword("");
    setCode("");
    setState("idle");
    if (outcome.at === "change-password") {
      setStep("change-password");
    } else if (outcome.at === "mfa") {
      setStep("code");
    } else {
      /* The server decides an enrolment challenge from the account, not from
         this address — so an operator can reach one here and is refused only
         once it completes. Without this branch the button spins forever. */
      setStep("enrol");
      try {
        setEnrolment((await onStartEnrolment?.()) ?? null);
      } catch {
        setState("failed");
        setStep("password");
      }
    }
  }

  async function submit() {
    onSubmit?.(email);
    if (!onSignIn) return;

    setState("signing-in");
    try {
      await follow(await onSignIn(email.trim(), password, remember));
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      /*
       * ⚠️ 0 IS "NO ANSWER AT ALL" — offline, DNS, timeout (`client.ts`). It is
       * separated from a refusal because a teacher on a dropped school
       * connection who reads "wrong password" will change a password that was
       * never wrong.
       */
      if (status === 429) setState("throttled");
      else if (status === 401 || status === 403) setState("refused");
      else if (typeof status === "number" && status >= 500)
        setState("server-fault");
      else setState("failed");
    }
  }

  async function submitCode() {
    if (!onSubmitCode) return;
    setState("signing-in");
    try {
      const outcome = await onSubmitCode(code.trim());
      /*
       * 🔴 THIS IS THE PATH THE REPORTED BUG TRAVELLED. A platform operator typed
       * a correct password and a correct authenticator code at the school admin's
       * address and was signed in, because only the operator door ever checked
       * what came back. The check now runs whichever door asked.
       */
      if (outcome.at === "wrong-door") refuseDoor(outcome.door);
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (status === 429) setState("throttled");
      /* An expired sign-in and a wrong code are the same 401 here, and the
         screen must not guess: `client.ts` discards the body. */
      else if (status === 401 || status === 403) setState("code-refused");
      else if (typeof status === "number" && status >= 500)
        setState("server-fault");
      else setState("failed");
    }
  }

  async function submitEnrolment() {
    if (!onFinishEnrolment) return;
    setState("signing-in");
    try {
      const outcome = await onFinishEnrolment(code.trim());
      if (outcome.at === "wrong-door") {
        refuseDoor(outcome.door);
        return;
      }
      setRecoveryCodes(outcome.codes);
      setCode("");
      setEnrolment(null);
      setStep("recovery");
      setState("idle");
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (status === 429) setState("throttled");
      else if (status === 401 || status === 403) setState("code-refused");
      else if (typeof status === "number" && status >= 500)
        setState("server-fault");
      else setState("failed");
    }
  }

  /**
   * P3-16 — ask for a reset link.
   *
   * 🔴 NO BRANCH ON THE ANSWER. The route says the same thing for every address,
   * so the screen does too; only a 429 and "no answer at all" are reported,
   * because both are about the request rather than about any account.
   */
  async function submitForgot() {
    if (!canRequestReset(forgotEmail) || forgotState === "sending") return;
    setForgotState("sending");
    try {
      await requestPasswordReset(forgotEmail.trim());
      setForgotState("sent");
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      setForgotState(status === 429 ? "throttled" : "failed");
    }
  }

  function openForgot() {
    setForgotEmail(email.trim());
    setForgotState("idle");
    setPassword("");
    setState("idle");
    setStep("forgot");
  }

  /*
   * 🔴 THE RECOVERY CODES ARE A WALL, AND THE ROUTER DELIBERATELY DOES NOT
   * NAVIGATE PAST THEM. They are the only copy that will ever exist, and the
   * operator has just made a second factor compulsory for themselves.
   */
  if (step === "recovery") {
    return (
      <Shell title="" subtitle="">
        <RecoveryCodesPanel
          codes={recoveryCodes}
          submitClass={submitClass}
          onDone={() => onEnterAfterEnrolment?.()}
        />
      </Shell>
    );
  }

  if (step === "change-password") {
    return (
      <Shell title="" subtitle="">
        <ChangePasswordStep
          idPrefix="staff"
          submitClass={submitClass}
          renderAlert={(children, testId) => (
            <Warn testId={testId}>{children}</Warn>
          )}
          onSubmit={async (next) => {
            if (!onChangePassword) return;
            await follow(await onChangePassword(next));
          }}
          onBack={() => {
            onCancelCode?.();
            setStep("password");
            setState("idle");
          }}
        />
      </Shell>
    );
  }

  if (step === "forgot") {
    const sent = forgotState === "sent";
    return (
      <Shell
        title={sent ? t.forgotSentTitle : t.forgotTitle}
        subtitle={sent ? t.forgotSent(forgotEmail.trim()) : t.forgotSubtitle}
      >
        {sent ? (
          <div className={pageLook ? "mt-6" : undefined}>
            <p
              data-testid="forgot-sent"
              className="rounded-md border border-line bg-surface px-3 py-2.5 text-[12.5px] leading-[18px] text-ink-soft"
            >
              {door === "school" ? t.forgotSentSchool : t.forgotSentTeacher}
            </p>
            <button
              type="button"
              onClick={() => {
                setStep("password");
              }}
              className={submitClass}
            >
              {t.forgotBack}
            </button>
          </div>
        ) : (
          <form
            className={pageLook ? "mt-6" : undefined}
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void submitForgot();
            }}
          >
            <label className={labelClass} htmlFor="staff-forgot-email">
              {pageLook ? t.workEmail : t.email}
            </label>
            <div className="mt-2">
              <TextInput
                id="staff-forgot-email"
                type="email"
                icon={IconMail}
                autoComplete="username"
                autoFocus
                placeholder={t.emailPlaceholder}
                value={forgotEmail}
                onChange={(e) => {
                  setForgotEmail(e.target.value);
                }}
              />
            </div>
            <button
              type="submit"
              disabled={
                forgotState === "sending" || !canRequestReset(forgotEmail)
              }
              aria-busy={forgotState === "sending" || undefined}
              className={submitClass}
            >
              {forgotState === "sending" ? t.forgotSending : t.forgotSubmit}
            </button>
            {forgotState === "throttled" && <Warn>{t.throttled}</Warn>}
            {forgotState === "failed" && <Warn>{t.failed}</Warn>}
            <p className="mt-5 text-center">
              <button
                type="button"
                onClick={() => {
                  setStep("password");
                }}
                className={linkClass}
              >
                {t.forgotBack}
              </button>
            </p>
          </form>
        )}
      </Shell>
    );
  }

  if (step === "enrol") {
    return (
      <Shell title="" subtitle="">
        <MfaEnrolmentBody
          idPrefix="staff"
          enrolment={enrolment}
          code={code}
          onCode={setCode}
          busy={busy}
          problem={
            state === "code-refused"
              ? "code"
              : state === "throttled"
                ? "throttled"
                : state === "failed" || state === "server-fault"
                  ? "failed"
                  : null
          }
          onSubmit={() => void submitEnrolment()}
          onBack={() => {
            onCancelCode?.();
            setStep("password");
            setCode("");
            setEnrolment(null);
            setState("idle");
          }}
          fieldClass={pageLook ? FIELD_BASE : FIELD}
          submitClass={submitClass}
          renderAlert={(children, testId) => (
            <Warn testId={testId}>{children}</Warn>
          )}
        />
      </Shell>
    );
  }

  /*
   * 🔴 THE CODE STEP REPLACES THE FORM RATHER THAN SITTING UNDER IT. A password
   * field still on screen beside a code field invites somebody to retype the
   * password when the code is refused — which cannot help, and which teaches
   * people to treat a second factor as a second password.
   */
  if (step === "code") {
    return (
      <Shell title={t.codeTitle} subtitle={t.codeSubtitle} footer={t.codeHint}>
        <form
          className={pageLook ? "mt-6" : undefined}
          onSubmit={(e) => {
            e.preventDefault();
            void submitCode();
          }}
        >
          <label className={labelClass} htmlFor="staff-mfa-code">
            {t.codeLabel}
          </label>
          <input
            id="staff-mfa-code"
            /* 🔴 `text`, NOT `number`: a recovery code has letters and a dash,
               and a number input silently strips the leading zero that one code
               in ten begins with. `inputMode` still opens the digit keypad. */
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={64}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className={`${pageLook ? `${FIELD_BASE} mt-2` : FIELD} tracking-[0.3em]`}
          />

          <button
            type="submit"
            disabled={busy || code.trim().length === 0}
            className={submitClass}
          >
            {busy ? t.signingIn : t.submit}
          </button>

          {state === "code-refused" && (
            <Warn testId="login-code-refused">{t.codeRefused}</Warn>
          )}
          {state === "throttled" && <Warn>{t.throttled}</Warn>}
          {state === "server-fault" && (
            <Warn testId="login-server-fault">{t.serverFault}</Warn>
          )}
          {state === "failed" && <Warn>{t.failed}</Warn>}

          <p className="mt-5 text-center">
            <button
              type="button"
              onClick={() => {
                onCancelCode?.();
                setStep("password");
                setCode("");
                setState("idle");
              }}
              className={linkClass}
            >
              {t.codeBack}
            </button>
          </p>
        </form>
      </Shell>
    );
  }

  const wrongDoorLine =
    rightDoor === "teacher"
      ? t.wrongDoorTeacher
      : rightDoor === "school"
        ? t.wrongDoorSchool
        : rightDoor === "operator"
          ? t.wrongDoorOperator
          : /* No staff door at all — `/auth/login` already refuses these, so this
               is a fallback rather than a state anybody should reach. */
            t.refused;

  return (
    <Shell
      title={t.title}
      subtitle={
        pageLook && onDoor
          ? t.consoleSubtitle
          : door === "school"
            ? t.subtitleSchool
            : t.subtitleTeacher
      }
      footer={
        /*
         * ⚠️ NOT UNDER "School admin", BECAUSE IT WOULD BE FALSE THERE. "Your
         * school admin sends an invitation" is how a TEACHER gets an account
         * (`InviteService`); a school's FIRST admin is provisioned by Kalqy —
         * `admin-panel.md` §3 — so printing it while "School admin" is chosen
         * tells a principal to ask themselves for an invitation. Saying nothing
         * is the honest half.
         */
        variant === "panel"
          ? door === "school"
            ? undefined
            : t.notePanel
          : /* The console page says it in the info box under the button. */
            undefined
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {/*
          ⚠️ ON THE CONSOLE PAGE THE DOOR TABS STAND WHERE A1's ROLE FIELD
          STANDS IN THE PANEL — and, like it, they choose a page, never a role.
        */}
        {pageLook && onDoor ? (
          <DoorTabs current={door} doors={doors} onDoor={onDoor} />
        ) : (
          roleField
        )}

        {pageLook ? (
          <div className="mt-6">
            <label className={labelClass} htmlFor="email">
              {t.workEmail}
            </label>
            <div className="mt-2">
              <TextInput
                id="email"
                type="email"
                icon={IconMail}
                autoComplete="username"
                placeholder={t.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <label className={`${labelClass} mt-5`} htmlFor="password">
              {t.password}
            </label>
            <div className="relative mt-2">
              <IconLock
                size={16}
                className="pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 text-ink-soft"
              />
              <PasswordInput
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={FIELD_WITH_ICON}
              />
            </div>

            {/* Figma P3's "Remember row": the box on the left, the reset on the right. */}
            <div className="mt-4 flex items-center justify-between gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 text-[13px] leading-5 text-ink">
                <input
                  type="checkbox"
                  data-testid="keep-signed-in"
                  checked={remember}
                  aria-describedby="staff-remember-hint"
                  onChange={(e) => {
                    setRemember(e.target.checked);
                  }}
                  className="h-4 w-4 rounded-[4px] border-line-strong accent-[var(--color-primary)]"
                />
                {t.remember}
              </label>
              <span id="staff-remember-hint" className="sr-only">
                {t.rememberHint}
              </span>
              <button
                type="button"
                data-testid="forgot-password"
                onClick={openForgot}
                className="rounded-sm text-[13px] font-medium text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink"
              >
                {t.forgot}
              </button>
            </div>
          </div>
        ) : (
          <>
            <label
              className="block text-dash-xs font-bold text-muted"
              htmlFor="email"
            >
              {t.email}
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={FIELD}
            />

            <label
              className="mt-5 block text-dash-xs font-bold text-muted"
              htmlFor="password"
            >
              {t.password}
            </label>
            <PasswordInput
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={FIELD}
            />
            {/* The same two controls as the console page, in this panel's look. */}
            <div className="mt-3 flex items-center justify-between gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 text-dash-xs font-bold text-ink-soft">
                <input
                  type="checkbox"
                  data-testid="keep-signed-in"
                  checked={remember}
                  aria-describedby="staff-remember-hint-panel"
                  onChange={(e) => {
                    setRemember(e.target.checked);
                  }}
                  className="h-4 w-4 accent-[var(--color-primary)]"
                />
                {t.remember}
              </label>
              <span id="staff-remember-hint-panel" className="sr-only">
                {t.rememberHint}
              </span>
              <button
                type="button"
                data-testid="forgot-password"
                onClick={openForgot}
                className="text-dash-xs font-bold text-ink-soft underline decoration-line-strong underline-offset-4 hover:text-ink"
              >
                {t.forgot}
              </button>
            </div>
          </>
        )}

        {/*
          🔴 NOTHING ABOUT A ROLE IS SUBMITTED HERE. `roleField` above is A1's
          form switch and stays in A1's state; the account decides. Anything
          sent would ask the client to nominate its own privileges.
        */}
        <button
          type="submit"
          disabled={
            busy ||
            blocked ||
            email.trim().length === 0 ||
            password.length === 0
          }
          className={submitClass}
        >
          {busy ? t.signingIn : t.submit}
        </button>

        {/* `role="alert"` so a screen reader is told, rather than a sighted
            user being the only one who learns the attempt failed. */}
        {state === "refused" && <Warn testId="login-refused">{t.refused}</Warn>}
        {/*
          🔴 A SEPARATE ALERT FROM `refused`, AND IT MUST STAY SEPARATE. Folding
          the two together would put "that email and password did not match" in
          front of somebody whose email and password matched — and the action
          that sentence prompts, resetting the password, is the one that leaves
          a school admin locked out of the tier they were trying to reach.
        */}
        {state === "wrong-door" && (
          <Warn testId="login-wrong-door">
            <span className="block font-bold">{t.wrongDoorTitle}</span>
            <span className="mt-1 block">{wrongDoorLine}</span>
            <span className="mt-1 block">{t.wrongDoorSignedOut}</span>
            {rightDoor !== null && onDoor && (
              <button
                type="button"
                onClick={() => {
                  onDoor(rightDoor);
                }}
                className={`mt-2 ${linkClass}`}
              >
                {t.wrongDoorGo}
              </button>
            )}
          </Warn>
        )}
        {state === "throttled" && <Warn>{t.throttled}</Warn>}
        {state === "server-fault" && (
          <Warn testId="login-server-fault">{t.serverFault}</Warn>
        )}
        {state === "failed" && <Warn>{t.failed}</Warn>}

        {/*
          ⚠️ TWO STAFF DOORS, so on its own page this link is the difference
          between "I opened the wrong one" and "my password stopped working". It
          is rendered whatever the form's state, because somebody who already
          knows they are at the wrong door should not have to be refused first in
          order to find out where to go.

          🔴 AND IT IS NOT RENDERED IN A1'S PANEL, WHERE THE ROLE CHOOSER IS THE
          SAME CONTROL. Two ways to say "actually I am the other one" — one that
          switches a select in place and one that navigates to another page —
          is a choice between identical outcomes, and the link is the worse of
          the two: it leaves the screen to do what the select does in place.
        */}
        {onDoor && variant !== "panel" && !pageLook && (
          <p className="mt-5 text-center">
            <button
              type="button"
              onClick={() => {
                onDoor(door === "school" ? "teacher" : "school");
              }}
              className="rounded-pill px-3 py-1.5 text-dash-xs font-bold text-ink-soft underline decoration-line-strong underline-offset-4 transition-colors duration-150 ease-dash hover:text-ink"
            >
              {door === "school"
                ? t.otherDoorFromSchool
                : t.otherDoorFromTeacher}
            </button>
          </p>
        )}

        {/*
          🔴 A SEPARATE BLOCK BELOW A RULE, NOT A THIRD LINK IN THE ROW ABOVE.
          The two links above are both *"you are at the wrong door"*; this one is
          *"you have no door yet"*, which is a different question and the only
          control on this screen that does not need an existing account. Folding
          it in would make three underlined sentences a reader has to tell apart.
        */}
        {/*
          Figma P3's info box. 🔴 ON THE SCHOOL DOOR IT POINTS AT THE ENQUIRY,
          which is how a school gets its first login (an operator sets it up).
          The self-serve sign-up link that sat under it is gone (P1-14, D7).
        */}
        {pageLook && (
          <div className="mt-5 flex items-start gap-3 rounded-md border border-line bg-surface px-4 py-3.5 shadow-[0_1px_1px_rgba(0,0,0,0.03)]">
            <IconSchool size={18} className="mt-px shrink-0 text-ink-soft" />
            <div className="min-w-0 text-[12px] leading-4 text-ink-soft">
              {door === "school" ? (
                <p>
                  {t.enquiryLead}{" "}
                  <a
                    href="#/contact-sales"
                    className="font-medium text-ink underline decoration-line-strong underline-offset-2 hover:decoration-ink"
                  >
                    {t.enquiryLink}
                  </a>{" "}
                  {t.enquiryTail}
                </p>
              ) : (
                <p>{t.notePanel}</p>
              )}
            </div>
          </div>
        )}
      </form>
    </Shell>
  );
}

/**
 * A3 — parent phone.
 *
 * 🔴 THE DIGITS ARE TABULAR, AND THAT IS NOT A DETAIL.
 *
 * This field rendered a ten-digit number at 15px in PROPORTIONAL figures, which
 * is the one place design-system.md §4 is unambiguous: *"tabular figures on
 * every stat"*. Proportional digits give a `1` a third of the width of a `8`,
 * so a phone number re-flows as it is typed and cannot be scanned in columns to
 * check against the one written on a form. A4 two functions down already sets
 * `tabular-nums` at 28px — these are consecutive screens in one flow, and they
 * looked like different products.
 *
 * ⚠️ THE FONT WAS NEVER THE PROBLEM, WHICH IS WORTH RECORDING. Tailwind's
 * preflight sets `font: inherit` on inputs, and Nunito's latin subset covers
 * U+0000–00FF, so the digits were already Nunito. They read as wrong because
 * they were small, light and proportional beside a 28px extrabold heading.
 */
/** M1/M2's left panel on a wide screen — what a parent gets from signing in. */
const PARENT_POINTS: readonly AuthPoint[] = [
  {
    icon: IconBell,
    title: "Your child’s week in a minute",
    body: "What they played, learned and how often they were at school.",
  },
  {
    icon: IconSupport,
    title: "Talk to the class teacher",
    body: "Messages, absences and the school’s notices in one place.",
  },
  {
    icon: IconShield,
    title: "Camera never records",
    body: "Movement is read on the school laptop and never leaves it.",
  },
];

export function ParentPhoneScreen({
  onSubmit,
  variant = "page",
  roleField,
  onRequestCode,
  viaSms = false,
}: {
  /** Its own address, or A1's panel. See `PanelShell`. */
  variant?: FormVariant;
  /** A1's role chooser, rendered above the number. See `StaffLoginScreen`. */
  roleField?: ReactNode;
  /** ⚠️ Kept for the specimen sheets, which render this screen with no API. */
  onSubmit?: (phone: string) => void;
  /** Given the E.164 number; resolves once the request has been ACCEPTED. */
  onRequestCode?: (phone: string) => Promise<void>;
  /**
   * 🔴 WHICH RAIL THE CODE TAKES, AND THE SCREEN MUST NOT GUESS IT. This said
   * "on WhatsApp" unconditionally; on a Firebase build the code arrives as an
   * ordinary SMS, and a parent told to look in WhatsApp waits for something
   * that is already sitting in their messages. The router knows which rail is
   * configured, so it says.
   */
  viaSms?: boolean;
}) {
  const [phone, setPhone] = useState("");
  const [state, setState] = useState<
    "idle" | "sending" | "throttled" | "misconfigured" | "failed"
  >("idle");
  const t = useCopy({
    en: {
      title: "Your phone number",
      help: viaSms
        ? "We will text you the code."
        : "We will send the code on WhatsApp.",
      submit: "Send code",
      sending: "Sending…",
      /*
       * ⚠️ 429 IS ABOUT THE REQUEST, NOT ABOUT THE NUMBER, so it gets its own
       * sentence. Five a minute per IP — a family sharing a connection, or a
       * school's, will meet it. "Wait a minute" and "check the number" are
       * opposite actions, and this one leaks nothing: the rate limit says
       * nothing about whether any number is registered.
       */
      throttled: "Too many requests from here. Wait a minute and try again.",
      /*
       * 🔴 NOT "CHECK YOUR CONNECTION", BECAUSE THE CONNECTION IS FINE. This is
       * a 503 from `firebasePhone.ts` — the project cannot send a code at all:
       * phone sign-in switched off, this origin missing from the authorised
       * domains, a restricted API key. A parent retrying, or moving to better
       * signal, will meet it every single time.
       *
       * ⚠️ SO IT NAMES WHO CAN FIX IT AND DOES NOT ASK THE PARENT TO TRY AGAIN.
       * The exact SDK code is in the console for whoever does.
       */
      misconfigured:
        "Text codes are not working right now. This is at Kalqy’s end, not yours — please tell your school, or try again later.",
      failed: "Couldn’t reach Kalqy. Check the connection and try again.",
      /*
       * 🔴 GENERIC, ALWAYS. "We couldn't find that number" tells an attacker
       * which parents exist — and these numbers belong to parents of identified
       * children. security.md's OTP rules require identical responses on request
       * AND verify.
       */
      generic: "If that number is registered, a code is on its way.",
      /*
       * 🔴 I1, ON THE SCREEN WHERE A PARENT DECIDES TO TRUST US. This is the
       * first Kalqy surface most parents ever see, and the question behind
       * "a camera, on my four-year-old" is answered here or not at all.
       * privacy-compliance.md §2: there is no video-recording feature, not
       * opt-in and not teacher-only.
       */
      note: "Kalqy never records video. The camera stays on your child’s device.",
    },
  });

  const complete = phone.length === 10;
  const Shell = variant === "panel" ? PanelShell : AuthPageShell;
  const busy = state === "sending";

  async function submit() {
    const e164 = `+91${phone}`;

    /*
     * 🔴 THE HOP HAPPENS AFTER THE SEND, NOT BEFORE IT — AND THAT ORDER WAS THE
     * WHOLE FAILURE. `onSubmit` navigates to the code screen, and it was called
     * on the FIRST line, before the code had been asked for. So every `setState`
     * below ran against a component the router had already unmounted, React
     * dropped them silently, and a send that threw — a Firebase project without
     * this origin in its authorised domains, a reCAPTCHA that did not load, a
     * 429 — moved the parent to a screen waiting for a code that was never sent.
     *
     * ⚠️ THE COMMENT BELOW ALREADY PROMISED THIS AND THE CODE COULD NOT KEEP IT:
     * *"a transport failure is the screen's to report (it shows 'wait a minute'
     * on a 429)"*. It cannot report anything it has already navigated away from.
     *
     * 🔴 AND ANTI-ENUMERATION IS UNTOUCHED, WHICH IS THE ONLY REASON THIS IS
     * SAFE TO REORDER. The rule is that a REGISTERED and an UNREGISTERED number
     * must be indistinguishable — and they still are, because the route answers
     * an identical generic 200 to both, so both resolve and both advance. What
     * no longer advances is a failure to SEND, which is identical for every
     * number and therefore says nothing about any of them.
     */
    if (!onRequestCode) {
      onSubmit?.(e164);
      return;
    }

    setState("sending");
    try {
      await onRequestCode(e164);
      /*
       * 🔴 NO SUCCESS STATE, AND NO BRANCH ON THE BODY. The route answers an
       * identical generic 200 for a registered number, a stranger's and
       * gibberish (threat T4) — so there is nothing here to distinguish, and
       * inventing a distinction would rebuild in the UI the phone-enumeration
       * oracle the endpoint was carefully built to prevent. The router advances
       * to the code screen either way.
       */
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (status === 429) setState("throttled");
      /* 🔴 503 IS A DEPLOYMENT FAULT, NOT A NETWORK ONE — the project cannot send
         a code at all, so "check the connection" sends a parent after the wrong
         thing. See `DEPLOYMENT_FAULTS` in `firebasePhone.ts`. */
      else if (status === 503) setState("misconfigured");
      /*
       * ⚠️ EVERY OTHER FAILURE IS "COULD NOT REACH", INCLUDING A 4xx. A refusal
       * this route can produce says nothing a parent can act on, and status 0 is
       * genuinely offline (`client.ts`). One sentence covers both honestly.
       */
      else setState("failed");
      /* ⚠️ AND STAY PUT. There is no code coming, so a code screen is a dead end
         — the parent would sit there typing digits that cannot be checked. */
      return;
    }

    onSubmit?.(e164);
  }

  /*
   * ✅ M1 · PARENT SIGN-IN (Figma 42:9138) — the console look for the full-page
   * door. The request, the `+91` prefix, the ten-digit gate and every refusal
   * are the same as the panel variant below; only the drawing changed. The
   * panel variant (the landing page's door picker) keeps its own look.
   */
  if (variant === "page") {
    return (
      <AuthSplit
        headline="Your child’s week, on your phone."
        points={PARENT_POINTS}
        testId="parent-signin-console"
      >
        <AuthHeading
          title="See how your child is learning"
          subtitle={
            viaSms
              ? "Sign in with the mobile number you gave the school. We send a code by text message."
              : "Sign in with the mobile number you gave the school. We send a code on WhatsApp."
          }
        />
        <form
          className="mt-6"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          {roleField}
          <label htmlFor="parent-phone" className={LABEL}>
            Mobile number
          </label>
          {/* The `+91` is context, not a typeable character (wireframes.md A3);
              the focus ring is on the wrapper so prefix and field read as one. */}
          {/* The input itself is 44px under a finger (40px with its frame under
              a mouse) — the field is the target, not the box drawn round it. */}
          <div className="mt-1.5 flex items-center gap-2 rounded-md border border-line-strong bg-surface px-3 transition-shadow duration-150 focus-within:border-ink focus-within:shadow-[0_0_0_3px_rgba(20,20,19,0.08)] pointer-fine:h-10">
            <IconDevice size={16} className="shrink-0 text-ink-soft" />
            <span
              aria-hidden="true"
              className="tabular font-dash text-[13px] text-ink"
            >
              +91
            </span>
            <input
              id="parent-phone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              maxLength={10}
              value={phone}
              placeholder="10-digit mobile number"
              onChange={(e) => {
                setPhone(e.target.value.replace(/\D/g, "").slice(0, 10));
              }}
              aria-describedby="parent-phone-note"
              className="h-[44px] min-w-0 flex-1 bg-transparent font-dash text-[13px] text-ink outline-none placeholder:text-muted pointer-fine:h-full"
            />
          </div>

          <button
            type="submit"
            disabled={!complete || busy}
            aria-busy={busy}
            className={`mt-4 ${SUBMIT_FULL}`}
          >
            {busy ? (
              t.sending
            ) : (
              <>
                <IconArrowRight size={16} />
                {viaSms ? "Send code by text" : "Send code on WhatsApp"}
              </>
            )}
          </button>

          {state === "throttled" && <ConsoleAlert>{t.throttled}</ConsoleAlert>}
          {state === "misconfigured" && (
            <ConsoleAlert>{t.misconfigured}</ConsoleAlert>
          )}
          {state === "failed" && <ConsoleAlert>{t.failed}</ConsoleAlert>}

          <div
            id="parent-phone-note"
            className="mt-4 flex items-start gap-2.5 rounded-lg border border-line bg-surface px-3.5 py-3"
          >
            <IconShield size={16} className="mt-px shrink-0 text-ink-soft" />
            <p className="font-dash text-[12px] leading-4 text-ink-soft">
              Only numbers the school linked to a child can sign in.{" "}
              <span data-testid="generic-copy">{t.generic}</span>
            </p>
          </div>
        </form>
      </AuthSplit>
    );
  }

  return (
    <Shell title={t.title} subtitle={t.help} footer={t.note}>
      {/*
        🔴 SUBMITTED WITH THE COUNTRY CODE THE FIELD DISPLAYS. `+91` is shown as
        a fixed prefix, so sending the bare ten digits would mean the screen and
        the request disagree about what was entered. `normalisePhone`
        (backend/src/modules/roster/phone.ts) peels +91, 0091, 91 and the trunk
        0 alike — this is the unambiguous spelling of the three.
      */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {roleField}

        {/*
          ⚠️ THE `+91` IS A LABEL, NOT AN EDITABLE CHARACTER — wireframes.md A3.
          A parent reads their own number as ten digits and should type exactly
          that; the country code is context, and making it typeable invites
          "+91 +91 98…". The focus ring is drawn on the WRAPPER so the prefix
          and the field read as one control.
        */}
        <div className="flex h-14 items-center rounded-lg border border-line-strong transition-colors duration-150 ease-dash focus-within:border-primary">
          <span
            aria-hidden="true"
            className="tabular border-r border-line-strong px-4 text-dash-md font-bold text-muted"
          >
            +91
          </span>
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            maxLength={10}
            value={phone}
            /*
             * Digits only, capped at ten. The backend rejects anything else
             * anyway; stripping here means a parent who pastes "98765 43210"
             * from a contact card gets a working field instead of a refusal.
             */
            onChange={(e) => {
              setPhone(e.target.value.replace(/\D/g, "").slice(0, 10));
            }}
            aria-label={t.title}
            className="tabular h-full min-w-0 flex-1 rounded-r-lg bg-transparent px-4 text-dash-xl font-extrabold tracking-[0.06em] text-ink outline-none"
          />
        </div>

        <button
          type="submit"
          /*
           * ⚠️ DISABLED UNTIL TEN DIGITS, WHICH IS THE ONE PLACE THIS FORM MAY
           * JUDGE THE INPUT. It is a length check, not a lookup — it says
           * nothing about whether the number is registered, so it cannot become
           * the enumeration oracle A3 exists to prevent.
           */
          disabled={!complete || busy}
          className={SUBMIT}
        >
          {busy ? t.sending : t.submit}
        </button>

        {state === "throttled" && <Alert>{t.throttled}</Alert>}
        {state === "misconfigured" && <Alert>{t.misconfigured}</Alert>}
        {state === "failed" && <Alert>{t.failed}</Alert>}

        <p className="mt-4 text-dash-xs text-muted" data-testid="generic-copy">
          {t.generic}
        </p>
      </form>
    </Shell>
  );
}

/** A4 — the code, with the number masked. */
export function OtpScreen({
  phone,
  onSubmit,
  onVerify,
  viaSms = false,
  sentAt = null,
  onResend,
}: {
  phone: string;
  /**
   * P2-03 — when the current code was asked for (epoch ms), for "Resend code in
   * 0:42". `null` when unknown (a specimen sheet), which offers a resend at once.
   */
  sentAt?: number | null;
  /**
   * Ask for a fresh code to the same number. Resolves once the request has been
   * ACCEPTED — `ParentPhoneScreen.onRequestCode`'s contract, and the same
   * generic answer whatever the number.
   */
  onResend?: () => Promise<void>;
  /** ⚠️ Kept for the specimen sheets, which render this screen with no API. */
  onSubmit?: (code: string) => void;
  /** Given the six digits; resolves when the parent session exists. */
  onVerify?: (code: string) => Promise<void>;
  /** Which rail sent it — see `ParentPhoneScreen`. It changes the footer, which
      quotes OUR challenge's rules and cannot quote Google's. */
  viaSms?: boolean;
}) {
  const [code, setCode] = useState("");
  const [state, setState] = useState<
    "idle" | "verifying" | "refused" | "expired" | "throttled" | "failed"
  >("idle");
  /*
   * 🔴 THE CODE THE SERVER HAS ALREADY REFUSED. Resubmitting it cannot succeed
   * and is not free: each attempt spends one of the five a challenge allows and
   * one of the ten a minute the IP allows. The reported failure was ten verifies
   * in four seconds, nine of them the same six digits — so the budget that would
   * have covered the correct code was gone before it was typed.
   */
  const [lastRefused, setLastRefused] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);
  /*
   * P2-03 — the resend control. `now` ticks once a second only while a wait is
   * running, so an idle code screen schedules nothing.
   */
  const [now, setNow] = useState(() => Date.now());
  const [resend, setResend] = useState<
    "idle" | "sending" | "sent" | "throttled" | "failed"
  >("idle");
  const waitSeconds = resendSecondsLeft(sentAt, now);
  const waiting = waitSeconds > 0;
  useEffect(() => {
    if (!waiting) return;
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [waiting, sentAt]);
  const t = useCopy({
    en: {
      title: "Enter the code",
      sentTo: "Sent to",
      submit: "Continue",
      verifying: "Checking…",
      /*
       * 🔴 ONE SENTENCE FOR FOUR REFUSALS, LIKE A2. The server answers an
       * identical 401 for a wrong code, an expired challenge, a challenge whose
       * five attempts are spent, and a number it has never seen — telling them
       * apart would say which numbers exist. So this names only the two things
       * a parent can actually do something about.
       */
      refused:
        "That code did not work. It may be wrong, or it may have expired.",
      /*
       * 🔴 THE ONE REFUSAL THAT IS NOT ABOUT THE CODE, AND THE ONLY ONE A PARENT
       * CAN FIX. It fires when no rail is holding a challenge for this number —
       * a reload drops the confirmation handle (`firebasePhone.ts` explains why
       * persisting it would be a credential at rest), and there is then nothing
       * to check against on either side.
       *
       * ⚠️ IT LEAKS NOTHING, WHICH IS WHY IT MAY BE SPECIFIC WHERE THE OTHERS
       * MAY NOT. The four refusals folded into one sentence are folded because
       * telling them apart would say which numbers are registered. This one is a
       * fact about THIS BROWSER — it says only that we are not holding a code
       * here, which is equally true of a registered number and a stranger's.
       */
      expired:
        "That code can no longer be checked. Go back and ask for a new one.",
      throttled: "Too many attempts from here. Wait a minute and try again.",
      failed: "Couldn’t reach Kalqy. Check the connection and try again.",
      /*
       * 🔴 BOTH NUMBERS ARE READ FROM THE SERVER'S OWN CONSTANTS, NOT ESTIMATED
       * — `backend/src/modules/auth/otp.ts`: OTP_TTL_MS = 5 × 60_000 and
       * MAX_ATTEMPTS = 5. A screen that says "a few minutes" about a five-minute
       * window teaches a parent to stop trusting the screen.
       */
      /*
       * ⚠️ AND IT STOPS QUOTING THEM WHEN THE CODE IS NOT OURS. On a Firebase
       * build the challenge belongs to Google: neither the five minutes nor the
       * five attempts are ours to promise, and a footer that states someone
       * else's limits as fact is the same broken trust from the other side.
       */
      note: viaSms
        ? "The code arrives by text. If it does not come, go back and check the number."
        : "The code lasts 5 minutes. After 5 wrong tries you will need a new one.",
      /* Figma M2. The wait is the server's own 60-second cooldown. */
      resendIn: (clock: string) => `Resend code in ${clock}`,
      resendNow: "Resend code",
      resending: "Sending…",
      /* Generic, like the first send: it says nothing about the number. */
      resent: "A new code is on its way.",
      resendThrottled:
        "Too many codes asked for this number. Wait a while, then try again.",
    },
  });

  const busy = state === "verifying";

  async function resendCode(): Promise<void> {
    if (!onResend || waitSeconds > 0 || resend === "sending") return;
    setResend("sending");
    try {
      await onResend();
      /* A fresh code: whatever was refused before is no longer the code to avoid. */
      setLastRefused(null);
      setCode("");
      setState("idle");
      setNow(Date.now());
      setResend("sent");
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      setResend(status === 429 ? "throttled" : "failed");
    }
  }

  async function submit() {
    onSubmit?.(code);
    if (!onVerify) return;
    /* ⚠️ A SECOND GUARD BEHIND THE DISABLED BUTTON. `Enter` held down on a
       focused form repeats at the keyboard's rate, and a disabled attribute
       applied on the next render is a frame too late to stop the burst. */
    if (!mayResubmit(code, lastRefused)) return;

    setState("verifying");
    try {
      await onVerify(code);
      // No success state: the router replaces this screen the moment the
      // session exists, exactly as A2 does.
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (status === 429) setState("throttled");
      /* 🔴 410 IS "NOTHING IS HOLDING A CODE FOR YOU", raised by the router when
         neither rail can check it — see `otpRail.ts`. It is the one refusal with
         an action attached, so it must not be folded into the generic sentence. */
      else if (status === 410) setState("expired");
      else if (status === 401 || status === 403) {
        setState("refused");
        setLastRefused(code);
      } else setState("failed");
    }
  }

  /*
   * ✅ M2 · ENTER THE CODE (Figma 42:9180), console look. ONE real input under
   * six drawn boxes: `autocomplete="one-time-code"` keeps the phone's own
   * code suggestion and paste working, and the verify / refusal / throttle
   * logic above is unchanged.
   */
  const active = Math.min(code.length, 5);
  return (
    <AuthSplit
      headline="Your child’s week, on your phone."
      points={PARENT_POINTS}
      testId="parent-otp-console"
    >
      <a
        href="#/parent/signin"
        className="-ml-1 mb-3 inline-flex items-center gap-1 rounded-md px-1 py-0.5 font-dash text-[13px] text-ink-soft hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink"
      >
        <IconChevronLeft size={16} /> Back
      </a>
      <AuthHeading
        title="Enter the 6-digit code"
        subtitle={
          <span data-testid="masked-phone">
            {viaSms ? "Sent by text to" : "Sent on WhatsApp to"}{" "}
            <span className="tabular font-medium text-ink">
              +91 {maskPhone(phone)}
            </span>
            .
          </span>
        }
      />
      <form
        className="mt-6"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="relative">
          <div aria-hidden="true" className="grid grid-cols-6 gap-2">
            {Array.from({ length: 6 }, (_, i) => (
              <span
                key={i}
                className={`tabular flex h-12 items-center justify-center rounded-md border bg-surface font-dash text-[18px] font-medium text-ink transition-colors duration-150 ${
                  i === active && focused && !busy
                    ? "border-ink shadow-[0_0_0_3px_rgba(20,20,19,0.08)]"
                    : "border-line-strong"
                }`}
              >
                {code[i] ?? ""}
              </span>
            ))}
          </div>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
            }}
            aria-label="6-digit code"
            aria-describedby="parent-otp-note"
            onFocus={() => {
              setFocused(true);
            }}
            onBlur={() => {
              setFocused(false);
            }}
            className="absolute inset-0 h-full w-full cursor-text bg-transparent text-transparent caret-transparent opacity-0 outline-none"
          />
        </div>
        <p
          id="parent-otp-note"
          className="mt-2 font-dash text-[12px] leading-4 text-muted"
        >
          {t.note}
        </p>
        <button
          type="submit"
          disabled={busy || !mayResubmit(code, lastRefused)}
          aria-busy={busy}
          className={`mt-4 ${SUBMIT_FULL}`}
        >
          {busy ? t.verifying : "Verify and continue"}
        </button>

        {state === "refused" && (
          <ConsoleAlert testId="otp-refused">{t.refused}</ConsoleAlert>
        )}
        {state === "expired" && (
          <ConsoleAlert testId="otp-expired">{t.expired}</ConsoleAlert>
        )}
        {state === "throttled" && <ConsoleAlert>{t.throttled}</ConsoleAlert>}
        {state === "failed" && <ConsoleAlert>{t.failed}</ConsoleAlert>}

        {/* ⚠️ `inline-flex`, because an inline link ignores the 44px touch
            floor `responsive.css` gives every other link. */}
        {onResend ? (
          <div className="mt-4 font-dash text-[12px] leading-4 text-muted">
            {waitSeconds > 0 ? (
              <p data-testid="otp-resend-wait" aria-live="off">
                <span className="tabular">
                  {t.resendIn(formatCountdown(waitSeconds))}
                </span>
              </p>
            ) : (
              <button
                type="button"
                data-testid="otp-resend"
                disabled={resend === "sending"}
                onClick={() => {
                  void resendCode();
                }}
                className={`inline-flex items-center ${LINK_QUIET}`}
              >
                {resend === "sending" ? t.resending : t.resendNow}
              </button>
            )}
            {resend === "sent" && (
              <p role="status" className="mt-2 text-ink-soft">
                {t.resent}
              </p>
            )}
            {resend === "throttled" && (
              <ConsoleAlert>{t.resendThrottled}</ConsoleAlert>
            )}
            {resend === "failed" && <ConsoleAlert>{t.failed}</ConsoleAlert>}
            <p className="mt-2">
              Wrong number?{" "}
              <a
                href="#/parent/signin"
                className={`inline-flex items-center ${LINK_QUIET}`}
              >
                Go back and change it
              </a>
            </p>
          </div>
        ) : (
          <p className="mt-4 font-dash text-[12px] leading-4 text-muted">
            No code?{" "}
            <a
              href="#/parent/signin"
              className={`inline-flex items-center ${LINK_QUIET}`}
            >
              Go back and send a new one
            </a>
          </p>
        )}
      </form>
    </AuthSplit>
  );
}
